const API_URL = import.meta.env.VITE_API_URL ?? ''

export type ApiUser = {
  id: string
  email: string
  firstName: string
  lastName: string
  roles: string[]
  preferredLocale: string
  phone?: string | null
  notifyEmailReminders?: boolean
  notifyWhatsapp?: boolean
  notifySms?: boolean
  notifyPush?: boolean
  preferredStyle?: string | null
  isEmailVerified: boolean
  tenantId: string | null
  hasImage?: boolean
  imageUrl?: string | null
}

type LoginResponse = {
  token: string
  refresh_token?: string
}

async function parseJson<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as T & {
    error?: string
    message?: string
    detail?: string
    title?: string
    violations?: Array<{ message?: string; title?: string }>
  }
  if (!res.ok) {
    const violation = data.violations?.[0]?.message || data.violations?.[0]?.title
    const apiCode = (data as { code?: string }).code
    const code = apiCode || data.error || data.message || violation || data.title
    const detail = typeof data.detail === 'string' ? data.detail.trim() : ''
    if (code && detail && detail !== code && !detail.startsWith(String(code))) {
      throw new Error(`${code}: ${detail}`)
    }
    throw new Error(code || detail || `HTTP ${res.status}`)
  }
  return data
}

export function getAccessToken(): string | null {
  return localStorage.getItem('ilia.accessToken')
}

export function getRefreshToken(): string | null {
  return localStorage.getItem('ilia.refreshToken')
}

export function setTokens(access: string, refresh?: string) {
  localStorage.setItem('ilia.accessToken', access)
  if (refresh) {
    localStorage.setItem('ilia.refreshToken', refresh)
  }
}

export function clearTokens() {
  localStorage.removeItem('ilia.accessToken')
  localStorage.removeItem('ilia.refreshToken')
}

let refreshInFlight: Promise<boolean> | null = null

/** Exchange refresh token for a new access token. Safe to call concurrently. */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return false
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!res.ok) {
        // Invalid/expired refresh — drop session. Network-ish 5xx: keep tokens.
        if (res.status === 401 || res.status === 403 || res.status === 400) {
          clearTokens()
        }
        return false
      }
      const data = (await res.json().catch(() => ({}))) as LoginResponse
      if (!data.token) return false
      setTokens(data.token, data.refresh_token)
      return true
    } catch {
      // Network error — keep existing tokens so the user stays logged in offline/transiently.
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

function isAuthPath(path: string): boolean {
  return (
    path.startsWith('/api/v1/auth/login')
    || path.startsWith('/api/v1/auth/google')
    || path.startsWith('/api/v1/auth/social-config')
    || path.startsWith('/api/v1/auth/refresh')
    || path.startsWith('/api/v1/auth/register')
  )
}

export async function apiFetch<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(init.headers)
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (!headers.has('Content-Type') && init.body && !isFormData) {
    headers.set('Content-Type', 'application/json')
  }
  const token = getAccessToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const res = await fetch(`${API_URL}${path}`, { ...init, headers })

  if (res.status === 401 && !retried && !isAuthPath(path)) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      return apiFetch<T>(path, init, true)
    }
    // refreshAccessToken already cleared tokens when the refresh token is invalid.
    // Do not clear again on transient failures so the session can recover.
  }

  return parseJson<T>(res)
}

/** Multipart upload with upload progress (XHR). */
export function apiUpload<T>(
  path: string,
  body: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_URL}${path}`)
    const token = getAccessToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.onprogress = (ev) => {
      if (!onProgress || !ev.lengthComputable || ev.total <= 0) return
      onProgress(Math.min(99, Math.round((ev.loaded / ev.total) * 100)))
    }
    xhr.onload = () => {
      void (async () => {
        try {
          if (xhr.status === 401) {
            const refreshed = await refreshAccessToken()
            if (refreshed) {
              resolve(await apiUpload<T>(path, body, onProgress))
              return
            }
          }
          const raw = xhr.responseText
          const data = (raw ? JSON.parse(raw) : {}) as T & { error?: string; message?: string }
          if (xhr.status >= 400) {
            reject(new Error(data.error || data.message || `http_${xhr.status}`))
            return
          }
          onProgress?.(100)
          resolve(data as T)
        } catch (e) {
          reject(e instanceof Error ? e : new Error('upload_failed'))
        }
      })()
    }
    xhr.onerror = () => reject(new Error('network_error'))
    xhr.onabort = () => reject(new Error('aborted'))
    xhr.send(body)
  })
}

export const authApi = {
  login: (email: string, password: string) =>
    apiFetch<LoginResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  googleLogin: (payload: { idToken?: string; accessToken?: string }) =>
    apiFetch<LoginResponse & { user: ApiUser }>('/api/v1/auth/google', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  socialConfig: () =>
    apiFetch<{ google: boolean; googleClientId: string | null; facebook: boolean; whatsapp: boolean }>(
      '/api/v1/auth/social-config',
    ),
  register: (payload: Record<string, unknown>) =>
    apiFetch<{ message: string; user: ApiUser }>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  me: () => apiFetch<{ user: ApiUser }>('/api/v1/auth/me'),
  verifyEmail: (token: string) =>
    apiFetch<{ message: string }>('/api/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  forgotPassword: (email: string) =>
    apiFetch<{ message: string }>('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, password: string) =>
    apiFetch<{ message: string }>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
}

export const contactApi = {
  send: (payload: { name: string; email: string; subject: string; message: string; locale: string }) =>
    apiFetch<{ message: string }>('/api/v1/contact', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

export const salonApi = {
  list: () => apiFetch<{ data: Array<{ id: string; name: string; slug: string; description: string | null; isPlatformBrand: boolean }> }>('/api/v1/salons'),
  branches: (slug: string) =>
    apiFetch<{ data: BranchDto[] }>(`/api/v1/salons/${slug}/branches`),
  services: (slug: string) =>
    apiFetch<{ data: ServiceDto[] }>(`/api/v1/salons/${slug}/services`),
  employees: (slug: string, branchId?: string) =>
    apiFetch<{ data: EmployeeDto[] }>(
      `/api/v1/salons/${slug}/employees${branchId ? `?branchId=${branchId}` : ''}`,
    ),
  googleReputation: (slug: string) =>
    apiFetch<{ data: GoogleReputationDto }>(`/api/v1/salons/${slug}/google-reputation`),
}

export type GoogleReviewItemDto = {
  id: string
  authorName: string
  authorPhotoUrl: string | null
  rating: number
  text: string
  relativeTime: string | null
  branchName: string | null
  branchCity: string | null
  mapsUrl: string | null
}

export type GoogleReputationDto = {
  url: string
  reviewUrl?: string
  rating: number
  count: number
  ratingLabel: string
  source: string
  updatedAt?: string | null
  reviews?: GoogleReviewItemDto[]
}

export type BranchDto = {
  id: string
  name: string
  slug: string
  addressLine: string | null
  city: string | null
  postalCode: string | null
  country?: string | null
  latitude?: number | null
  longitude?: number | null
  phone: string | null
  email?: string | null
  openingHours: Record<string, { open: string; close: string } | null>
  hasImage?: boolean
  imageUrl?: string | null
  isActive?: boolean
  googlePlaceId?: string | null
  googleBusinessLocationId?: string | null
  googleMapsUrl?: string | null
  googleReviewUrl?: string | null
  showGoogleReviews?: boolean
}

export type ServiceDto = {
  id: string
  name: string
  slug: string
  description: string | null
  durationMinutes: number
  priceCents: number
  price: string
  currency: string
  category: string | null
}

export type EmployeeDto = {
  id: string
  fullName: string
  title: string | null
  bio: string | null
  languages: string[]
  branchId: string
  branchName: string
  serviceIds: string[]
  isBookable: boolean
  weeklyOffWeekdays?: number[]
  hasImage?: boolean
  imageUrl?: string | null
}

export type SlotDto = {
  start: string
  end: string
  employeeId: string
  employeeName: string
  employeeImageUrl?: string | null
  available?: boolean
  reason?: 'booked' | 'past' | string | null
}

export type AvailabilityAlternative = {
  employeeId: string
  employeeName: string
  employeeImageUrl?: string | null
  slots: SlotDto[]
}

export type AvailabilityResponse = {
  data: SlotDto[]
  preferredUnavailable?: boolean
  unavailable?: { code: string; message: string; timeOffType: string | null; holidayName?: string | null } | null
  alternatives?: AvailabilityAlternative[]
  meta?: { serviceCount: number; totalDurationMinutes: number }
}

export type BookingDto = {
  id: string
  status: string
  startsAt: string
  endsAt: string
  branchId?: string
  branchName: string
  branchAddress?: string | null
  branchCity?: string | null
  branchPostalCode?: string | null
  branchImageUrl?: string | null
  employeeId?: string | null
  employeeName: string | null
  employeeImageUrl?: string | null
  total: string
  currency: string
  freeHaircutRedeemed?: boolean
  canReview?: boolean
  hasReview?: boolean
  noShowFeeCents?: number
  noShowFeeStatus?: string | null
  customerAttendance?: string | null
  actionToken?: string | null
  items: Array<{ serviceName: string; durationMinutes: number; priceCents: number }>
}

export const bookingApi = {
  availability: (params: {
    branchId: string
    serviceId?: string
    serviceIds?: string[]
    date: string
    employeeId?: string
    locale?: string
  }) => {
    const ids = (params.serviceIds?.length ? params.serviceIds : params.serviceId ? [params.serviceId] : [])
      .map((id) => id.trim())
      .filter(Boolean)
    const q = new URLSearchParams({
      branchId: params.branchId,
      date: params.date,
    })
    if (ids.length === 1) {
      q.set('serviceId', ids[0])
    }
    if (ids.length > 0) {
      q.set('serviceIds', ids.join(','))
    }
    if (params.employeeId) q.set('employeeId', params.employeeId)
    if (params.locale) q.set('locale', params.locale)
    return apiFetch<AvailabilityResponse>(`/api/v1/availability?${q}`)
  },
  create: (payload: Record<string, unknown>) =>
    apiFetch<{ data: BookingDto }>('/api/v1/bookings', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  checkout: (payload: Record<string, unknown>) =>
    apiFetch<{
      data: {
        booking: BookingDto
        paymentId: string
        status: string
        method: string
        amount: string
        currency: string
        checkoutUrl: string | null
        stripeEnabled: boolean
        mode: string
      }
    }>('/api/v1/bookings/checkout', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  mine: () => apiFetch<{ data: BookingDto[] }>('/api/v1/customer/bookings'),
  cancel: (id: string, reason: string) =>
    apiFetch<{ data: BookingDto }>(`/api/v1/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  reschedule: (id: string, payload: { startsAt: string; employeeId?: string }) =>
    apiFetch<{ data: BookingDto }>(`/api/v1/bookings/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  joinWaitlist: (payload: Record<string, unknown>) =>
    apiFetch<{ data: WaitlistEntryDto }>('/api/v1/waitlist', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  createReview: (payload: { bookingId: string; rating: number; comment?: string; isPublic?: boolean }) =>
    apiFetch<{ data: ReviewDto }>('/api/v1/reviews', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

export type PublicBookingActionDto = {
  id: string
  status: string
  customerAttendance?: string | null
  startsAt: string
  endsAt: string
  branchName: string
  employeeName?: string | null
  customerName: string
  items: Array<{ serviceName: string; durationMinutes: number }>
  actionToken?: string | null
}

export const bookingActionApi = {
  show: (token: string) =>
    apiFetch<{ data: PublicBookingActionDto }>(`/api/v1/public/bookings/action/${encodeURIComponent(token)}`),
  confirm: (token: string) =>
    apiFetch<{ ok: boolean; data: PublicBookingActionDto }>(
      `/api/v1/public/bookings/action/${encodeURIComponent(token)}/confirm`,
      { method: 'POST', body: '{}' },
    ),
  cancel: (token: string, reason = 'customer_link_cancel') =>
    apiFetch<{ ok: boolean; data: PublicBookingActionDto }>(
      `/api/v1/public/bookings/action/${encodeURIComponent(token)}/cancel`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
}

export type WaitlistEntryDto = {
  id: string
  status: string
  customerName: string
  customerEmail: string
  customerPhone: string | null
  branchId: string
  branchName: string
  serviceId: string | null
  serviceName: string | null
  preferredDate: string | null
  notes: string | null
  notifiedAt: string | null
  createdAt: string
}

export type ReviewDto = {
  id: string
  rating: number
  comment: string | null
  isPublic: boolean
  createdAt: string
  customerName: string
  bookingId?: string
  bookingWhen?: string
}

export const profileApi = {
  update: (payload: {
    firstName?: string
    lastName?: string
    preferredLocale?: string
    phone?: string | null
    notifyEmailReminders?: boolean
    notifyWhatsapp?: boolean
    notifySms?: boolean
    notifyPush?: boolean
    preferredStyle?: string | null
  }) =>
    apiFetch<{ user: ApiUser }>('/api/v1/customer/profile', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  uploadImage: (file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ user: ApiUser }>('/api/v1/customer/profile/image', { method: 'POST', body })
  },
  deleteImage: () =>
    apiFetch<{ user: ApiUser }>('/api/v1/customer/profile/image', { method: 'DELETE' }),
}

export type DisplayBoardDto = {
  branch: { id: string; name: string; city: string | null; addressLine: string | null }
  date: string
  generatedAt: string
  bookingCount: number
  columns: Array<{
    employeeId: string
    fullName: string
    title: string | null
    imageUrl: string | null
    bookings: DisplayBookingCard[]
  }>
  unassigned: DisplayBookingCard[]
}

export type DisplayBookingCard = {
  id: string
  startsAt: string
  endsAt: string
  status: string
  customerName: string
  customerPhone: string | null
  customerNotes: string | null
  customerImageUrl: string | null
  services: string[]
  total: string
  currency: string
  paymentStatus?: string | null
  paid?: boolean
}

export const displayApi = {
  board: (branchId: string, date?: string) => {
    const q = new URLSearchParams({ branchId })
    if (date) q.set('date', date)
    return apiFetch<{ data: DisplayBoardDto }>(`/api/v1/display/board?${q}`)
  },
}

export const paymentApi = {
  config: (salonSlug?: string) => {
    const q = salonSlug && salonSlug !== 'ilia-barbershop' ? `?salon=${encodeURIComponent(salonSlug)}` : ''
    return apiFetch<{
      stripeEnabled: boolean
      stripeCanCharge?: boolean
      salonPaymentsReady?: boolean
      requiresConnect?: boolean
      applePayReady?: boolean
      methods: string[]
    }>(`/api/v1/payments/config${q}`)
  },
  create: (payload: { bookingId: string; method: string; depositOnly?: boolean; couponCode?: string }) =>
    apiFetch<{
      data: {
        paymentId: string
        status: string
        method: string
        amount: string
        currency: string
        checkoutUrl: string | null
        stripeEnabled: boolean
        mode: string
      }
    }>('/api/v1/payments/create', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  confirmBooking: (sessionId: string) =>
    apiFetch<{ data: { status: string; bookingId: string | null } }>('/api/v1/payments/confirm-booking', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    }),
}

export type SaasPlanDto = {
  id: string
  name: string
  slug: string
  description: string | null
  monthlyPrice: string
  monthlyPriceCents?: number
  commitmentMonths: number
  contractTotal: string
  maxBranches: number
  maxEmployees: number
  isFeatured: boolean
  isActive?: boolean
  sortOrder?: number
  features: string[]
  hasImage?: boolean
  imageUrl?: string | null
  imageMime?: string | null
}

export const saasApi = {
  plans: () => apiFetch<{ data: SaasPlanDto[]; vatNote: string }>('/api/v1/plans'),
  planDetail: (id: string) =>
    apiFetch<{
      data: SaasPlanDto & {
        details?: {
          includes: string[]
          dashboardFeatures: string[]
          binding: boolean
          earlyCancel: boolean
          saleException: boolean
        }
      }
      vatNote: string
      subscribeSteps: string[]
      cancelPolicy: {
        noEarlyCancel: boolean
        exceptionSaleOfBusiness: boolean
        continuesOnNewLocation: boolean
        jurisdiction: string
      }
    }>(`/api/v1/plans/${id}`),
  contractPreview: (payload: Record<string, unknown>) =>
    apiFetch<{ data: { html: string } }>('/api/v1/contracts/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  subscribe: (payload: Record<string, unknown>) =>
    apiFetch<{
      message: string
      data: {
        loginEmail: string
        subscriptionId: string
        contractId: string
        contractPdfUrl: string
        checkoutUrl?: string | null
        requiresPayment?: boolean
        status?: string
      }
    }>('/api/v1/subscriptions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  confirmCheckout: (sessionId: string) =>
    apiFetch<{ data: { subscriptionId: string; status: string } }>('/api/v1/subscriptions/confirm-checkout', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    }),
  paySubscription: () =>
    apiFetch<{ data: { checkoutUrl: string } }>('/api/v1/tenant/subscription/pay', { method: 'POST' }),
  mySubscription: () =>
    apiFetch<{ data: null | Record<string, unknown> }>('/api/v1/tenant/subscription'),
  cancel: () =>
    apiFetch<{ data: { status: string } }>('/api/v1/subscriptions/cancel', { method: 'POST' }),
  dashboard: () =>
    apiFetch<{
      data: {
        salonCount: number
        branchCount: number
        employeeCount: number
        todayBookings: number
        todayRevenue?: string
        todayRevenueCents?: number
        todayCustomers?: number
        limits?: {
          branches: { used: number; max: number; remaining: number; atCap: boolean }
          employees: { used: number; max: number; remaining: number; atCap: boolean }
        }
        onboarding?: {
          hasBranch: boolean
          hasEmployee: boolean
          hasService: boolean
          subscriptionActive: boolean
          canPublishWebsite?: boolean
          complete: boolean
        }
        canPublishWebsite?: boolean
        subscription: null | {
          status: string
          plan: string
          nextBillingAt: string | null
          endsAt: string
          autoRenew?: boolean
          monthlyPrice?: string
        }
      }
    }>('/api/v1/tenant/dashboard'),
}

export type EmailSettingsDto = {
  fromAddress: string
  bookingInbox: string
  notifyBookingInbox: boolean
  notifySalonOwners: boolean
  notifyBranchEmail: boolean
  notifyAdminOnSalonSignup: boolean
  defaultLocale: string
}

export type HomepageHeroSlideAdminDto = {
  id: string
  slot: number
  position: number
  sortOrder: number
  isActive: boolean
  altDe: string | null
  altAr: string | null
  hasCustomImage: boolean
  imageUrl: string
  png768: string
  webp480: string
  webp768: string
  webp1024: string
  width: number
  height: number
  defaultAsset?: string
  imageUpdatedAt?: string | null
  updatedAt?: string
}

export const homepageApi = {
  heroSlides: () =>
    apiFetch<{ data: HomepageHeroSlideAdminDto[] }>('/api/v1/homepage/hero-slides'),
}

export type WhatsAppStatusDto = {
  serviceConfigured: boolean
  serviceOnline: boolean
  connected: boolean
  canSend?: boolean
  status: string
  phone?: string | null
  lastConnectedAt?: string | null
  lastDisconnectedAt?: string | null
  lastError?: string | null
}

export type WhatsAppQrDto = {
  connected?: boolean
  status?: string
  qr?: string
  phone?: string
}

export type WhatsAppSettingsDto = {
  enabled: boolean
  appointmentCreated: boolean
  appointmentConfirmed: boolean
  appointmentChanged: boolean
  appointmentCancelled: boolean
  paymentConfirmed: boolean
  paymentFailed: boolean
  reminder24h: boolean
  reminder3h: boolean
  reminder24hHours: number
  reminder3hHours: number
}

export type WhatsAppLogDto = {
  id: string
  createdAt: string
  type: string
  status: string
  phone?: string | null
  message: string
  attempts: number
  sentAt?: string | null
  error?: string | null
  appointmentId?: string | null
}

export const adminApi = {
  getEmailSettings: () => apiFetch<{ data: EmailSettingsDto }>('/api/v1/admin/email-settings'),
  saveEmailSettings: (payload: Partial<EmailSettingsDto>) =>
    apiFetch<{ data: EmailSettingsDto }>('/api/v1/admin/email-settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  testEmailSettings: (to?: string) =>
    apiFetch<{ data: { sent: boolean; to: string } }>('/api/v1/admin/email-settings/test', {
      method: 'POST',
      body: JSON.stringify(to ? { to } : {}),
    }),
  getWhatsAppStatus: () => apiFetch<{ data: WhatsAppStatusDto }>('/api/v1/admin/whatsapp/status'),
  getWhatsAppQr: () => apiFetch<{ data: WhatsAppQrDto }>('/api/v1/admin/whatsapp/qr'),
  disconnectWhatsApp: () => apiFetch<{ data: Record<string, unknown> }>('/api/v1/admin/whatsapp/disconnect', { method: 'POST' }),
  getWhatsAppSettings: () => apiFetch<{ data: WhatsAppSettingsDto }>('/api/v1/admin/whatsapp/settings'),
  saveWhatsAppSettings: (payload: Partial<WhatsAppSettingsDto>) =>
    apiFetch<{ data: WhatsAppSettingsDto }>('/api/v1/admin/whatsapp/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  testWhatsApp: (payload: { phone?: string; message?: string }) =>
    apiFetch<{ data: { success: boolean; status?: string; error?: string } }>('/api/v1/admin/whatsapp/test', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getWhatsAppLogs: (params?: Record<string, string>) => {
    const q = params ? `?${new URLSearchParams(params)}` : ''
    return apiFetch<{ data: WhatsAppLogDto[] }>(`/api/v1/admin/whatsapp/logs${q}`)
  },
  retryWhatsAppNotification: (id: string) =>
    apiFetch<{ data: { queued: boolean } }>(`/api/v1/admin/whatsapp/logs/${id}/retry`, { method: 'POST' }),
  listPlans: () => apiFetch<{ data: SaasPlanDto[] }>('/api/v1/admin/plans'),
  updatePlan: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: SaasPlanDto }>(`/api/v1/admin/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  uploadPlanImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: SaasPlanDto }>(`/api/v1/admin/plans/${id}/image`, {
      method: 'POST',
      body,
    })
  },
  deletePlanImage: (id: string) =>
    apiFetch<{ data: SaasPlanDto }>(`/api/v1/admin/plans/${id}/image`, { method: 'DELETE' }),
  listBranches: (salon = 'ilia-barbershop') =>
    apiFetch<{
      data: BranchDto[]
      employees: Array<{
        id: string
        firstName: string
        lastName: string
        fullName: string
        title: string | null
        branchId: string
        branchName: string
        isBookable: boolean
        isActive: boolean
        weeklyOffWeekdays?: number[]
        hasImage?: boolean
        imageUrl?: string | null
      }>
      salon: { id: string; name: string; slug: string }
    }>(`/api/v1/admin/branches?salon=${encodeURIComponent(salon)}`),
  updateBranch: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: BranchDto }>(`/api/v1/admin/branches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  uploadBranchImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: BranchDto }>(`/api/v1/admin/branches/${id}/image`, { method: 'POST', body })
  },
  deleteBranchImage: (id: string) =>
    apiFetch<{ data: BranchDto }>(`/api/v1/admin/branches/${id}/image`, { method: 'DELETE' }),
  createBranch: (payload: Record<string, unknown>) =>
    apiFetch<{ data: BranchDto }>('/api/v1/admin/branches', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteBranch: (id: string) =>
    apiFetch<{ data: BranchDto; deleted: boolean }>(`/api/v1/admin/branches/${id}`, { method: 'DELETE' }),
  googleReviewsSettings: () =>
    apiFetch<{ data: { displayLimit: number; cacheHours: number; placesApiKeySet?: boolean } }>('/api/v1/admin/google-reviews/settings'),
  saveGoogleReviewsSettings: (payload: { displayLimit: number; cacheHours: number; placesApiKey?: string }) =>
    apiFetch<{ data: { displayLimit: number; cacheHours: number; placesApiKeySet?: boolean } }>('/api/v1/admin/google-reviews/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  refreshGoogleReviews: () =>
    apiFetch<{ data: { rating: number | null; count: number | null; reviews: number; updatedAt: string | null; source: string | null } }>(
      '/api/v1/admin/google-reviews/refresh',
      { method: 'POST' },
    ),
  updateEmployee: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: Record<string, unknown> }>(`/api/v1/admin/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  createEmployee: (payload: Record<string, unknown>) =>
    apiFetch<{ data: Record<string, unknown> }>('/api/v1/admin/employees', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteEmployee: (id: string) =>
    apiFetch<{ data: Record<string, unknown>; deleted: boolean }>(`/api/v1/admin/employees/${id}`, {
      method: 'DELETE',
    }),
  uploadEmployeeImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: Record<string, unknown> }>(`/api/v1/admin/employees/${id}/image`, {
      method: 'POST',
      body,
    })
  },
  deleteEmployeeImage: (id: string) =>
    apiFetch<{ data: Record<string, unknown> }>(`/api/v1/admin/employees/${id}/image`, {
      method: 'DELETE',
    }),
  listEmployeeTimeOffs: (id: string) =>
    apiFetch<{
      data: Array<{ id: string; type: string; startsOn: string; endsOn: string; reason: string | null }>
      weeklyOffWeekdays: number[]
    }>(`/api/v1/admin/employees/${id}/time-offs`),
  createEmployeeTimeOff: (
    id: string,
    payload: { type: string; startsOn: string; endsOn: string; reason?: string } | { weeklyOffWeekdays: number[] },
  ) =>
    apiFetch<{ data: Record<string, unknown> | null; weeklyOffWeekdays?: number[] }>(
      `/api/v1/admin/employees/${id}/time-offs`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  deleteTimeOff: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/admin/time-offs/${id}`, { method: 'DELETE' }),

  listHomepageHeroSlides: () =>
    apiFetch<{ data: HomepageHeroSlideAdminDto[] }>('/api/v1/admin/homepage/hero-slides'),
  patchHomepageHeroSlide: (id: string, payload: Partial<HomepageHeroSlideAdminDto> & Record<string, unknown>) =>
    apiFetch<{ data: HomepageHeroSlideAdminDto }>(`/api/v1/admin/homepage/hero-slides/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  reorderHomepageHeroSlides: (ids: string[]) =>
    apiFetch<{ data: HomepageHeroSlideAdminDto[] }>('/api/v1/admin/homepage/hero-slides/reorder', {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),
  uploadHomepageHeroSlideImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: HomepageHeroSlideAdminDto }>(`/api/v1/admin/homepage/hero-slides/${id}/image`, {
      method: 'POST',
      body,
    })
  },
  resetHomepageHeroSlideImage: (id: string) =>
    apiFetch<{ data: HomepageHeroSlideAdminDto }>(`/api/v1/admin/homepage/hero-slides/${id}/image`, {
      method: 'DELETE',
    }),

  overview: () =>
    apiFetch<{
      data: {
        users: number
        customers: number
        activeMemberships: number
        services: number
        products: number
        activeSaasSubscriptions?: number
        salon: { id: string; name: string; slug: string }
        partnerSalons?: AdminPartnerSalonDto[]
        analytics?: {
          month: string
          monthLabel: string
          revenueThisMonth: {
            total: string
            totalCents: number
            booking: string
            shop: string
            club: string
            saas: string
            payments: number
          }
          revenuePrevMonth: { total: string; totalCents: number }
          revenueChangePercent: number
          visitsThisMonth: number
          completedVisits: number
          cancelledVisits: number
          noShowVisits: number
          purchasesThisMonth: number
          newUsersThisMonth: number
          newMembershipsThisMonth: number
          revenueByMonth: Array<{
            month: string
            label: string
            total: string
            totalCents: number
            booking: string
            shop: string
            club: string
            saas: string
            payments: number
          }>
          visitsByDay: Record<string, number>
        }
      }
    }>('/api/v1/admin/overview'),
  listSalons: () => apiFetch<{ data: AdminPartnerSalonDto[] }>('/api/v1/admin/salons'),
  listUsers: (params?: { q?: string; role?: string }) => {
    const q = new URLSearchParams()
    if (params?.q) q.set('q', params.q)
    if (params?.role) q.set('role', params.role)
    const qs = q.toString()
    return apiFetch<{ data: AdminUserDto[] }>(`/api/v1/admin/users${qs ? `?${qs}` : ''}`)
  },
  createUser: (payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminUserDto }>('/api/v1/admin/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateUser: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminUserDto }>(`/api/v1/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteUser: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/admin/users/${id}`, { method: 'DELETE' }),
  listMemberships: (params?: { q?: string; status?: string }) => {
    const q = new URLSearchParams()
    if (params?.q) q.set('q', params.q)
    if (params?.status) q.set('status', params.status)
    const qs = q.toString()
    return apiFetch<{ data: AdminMembershipDto[] }>(`/api/v1/admin/memberships${qs ? `?${qs}` : ''}`)
  },
  updateMembership: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: { id: string; status: string; remainingUses: number; autoRenew: boolean } }>(
      `/api/v1/admin/memberships/${id}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
  deleteMembership: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/admin/memberships/${id}`, { method: 'DELETE' }),
  listServices: () => apiFetch<{ data: AdminServiceDto[] }>('/api/v1/admin/services'),
  createService: (payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminServiceDto }>('/api/v1/admin/services', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateService: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminServiceDto }>(`/api/v1/admin/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteService: (id: string) =>
    apiFetch<{ deleted: boolean; soft?: boolean }>(`/api/v1/admin/services/${id}`, { method: 'DELETE' }),
  uploadServiceImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: AdminServiceDto }>(`/api/v1/admin/services/${id}/image`, { method: 'POST', body })
  },
  deleteServiceImage: (id: string) =>
    apiFetch<{ data: AdminServiceDto }>(`/api/v1/admin/services/${id}/image`, { method: 'DELETE' }),
  listProducts: () => apiFetch<{ data: AdminProductDto[] }>('/api/v1/admin/products'),
  createProduct: (payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminProductDto }>('/api/v1/admin/products', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateProduct: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminProductDto }>(`/api/v1/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteProduct: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/admin/products/${id}`, { method: 'DELETE' }),
  uploadProductImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: AdminProductDto }>(`/api/v1/admin/products/${id}/image`, { method: 'POST', body })
  },
  deleteProductImage: (id: string) =>
    apiFetch<{ data: AdminProductDto }>(`/api/v1/admin/products/${id}/image`, { method: 'DELETE' }),
  listShopOrders: (params?: { status?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return apiFetch<{ data: ShopOrderDto[] }>(`/api/v1/admin/shop-orders${qs ? `?${qs}` : ''}`)
  },
  markShopOrderPaid: (id: string) =>
    apiFetch<{ data: ShopOrderDto }>(`/api/v1/admin/shop-orders/${id}/mark-paid`, { method: 'POST' }),

  listCoupons: () => apiFetch<{ data: AdminCouponDto[] }>('/api/v1/admin/coupons'),
  createCoupon: (payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminCouponDto }>('/api/v1/admin/coupons', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCoupon: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminCouponDto }>(`/api/v1/admin/coupons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteCoupon: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/admin/coupons/${id}`, { method: 'DELETE' }),

  listLoyaltyRequests: (status = 'pending') =>
    apiFetch<{
      data: Array<{
        id: string
        status: string
        requestDate: string
        createdAt: string
        customer: { id: string; fullName: string; email: string }
        loyalty: { points: number; stamps: number; freeHaircuts: number }
      }>
    }>(`/api/v1/admin/loyalty/requests?status=${encodeURIComponent(status)}`),
  approveLoyaltyRequest: (id: string) =>
    apiFetch<{ data: { points: number; stamps: number; freeHaircuts: number } }>(
      `/api/v1/admin/loyalty/requests/${id}/approve`,
      { method: 'POST' },
    ),
  rejectLoyaltyRequest: (id: string) =>
    apiFetch<{ data: { status: string } }>(`/api/v1/admin/loyalty/requests/${id}/reject`, {
      method: 'POST',
    }),
  searchLoyaltyCustomers: (q = '') => {
    const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
    return apiFetch<{
      data: Array<{
        id: string
        fullName: string
        email: string
        points: number
        stamps: number
        freeHaircuts: number
        canStampToday: boolean
        pendingRequest: boolean
      }>
    }>(`/api/v1/admin/loyalty/customers${qs}`)
  },
  stampLoyaltyCustomer: (userId: string) =>
    apiFetch<{ data: { points: number; stamps: number; freeHaircuts: number } }>(
      `/api/v1/admin/loyalty/customers/${userId}/stamp`,
      { method: 'POST' },
    ),
  listLoyaltyEligible: () =>
    apiFetch<{
      data: Array<{
        bookingId: string
        startsAt: string
        endsAt: string
        eligibleAt: string
        status: string
        branchName: string
        employeeName: string | null
        serviceNames: string[]
        customer: { id: string; fullName: string; email: string } | null
        loyalty: { points: number; stamps: number; freeHaircuts: number }
      }>
    }>('/api/v1/admin/loyalty/eligible'),
  stampLoyaltyBooking: (bookingId: string) =>
    apiFetch<{ data: { points: number; stamps: number; freeHaircuts: number } }>(
      `/api/v1/admin/loyalty/bookings/${bookingId}/stamp`,
      { method: 'POST' },
    ),
  redeemFreeHaircut: (bookingId: string) =>
    apiFetch<{ data: { id: string; totalCents: number; total: string; freeHaircutRedeemed: boolean } }>(
      `/api/v1/admin/bookings/${bookingId}/redeem-free-haircut`,
      { method: 'POST' },
    ),
}

export type AdminPartnerSalonDto = {
  id: string
  name: string
  slug: string
  publicPath: string
  siteStatus: string
  isActive: boolean
  ownerName: string | null
  ownerEmail: string | null
  planName: string | null
  subscriptionStatus: string | null
  monthlyPrice: string | null
  createdAt: string
}

export type AdminUserDto = {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  roles: string[]
  preferredLocale: string
  isActive: boolean
  isEmailVerified?: boolean
  createdAt?: string
  imageUrl?: string | null
}

export type AdminMembershipDto = {
  id: string
  status: string
  plan: string
  planId: string
  tier: string
  customerName: string
  customerEmail: string
  customerId: string
  remainingUses: number
  includedUses: number
  usedUses: number
  startsAt: string
  endsAt: string
  paymentMethod: string
  paymentStatus: string
  amountPaid: string
  invoiceNumber: string | null
  isUsable: boolean
  isExhausted: boolean
  autoRenew: boolean
  createdAt: string
}

export type AdminServiceDto = {
  id: string
  name: string
  slug?: string
  description: string | null
  durationMinutes: number
  priceCents?: number
  price: string
  isActive: boolean
  sortOrder?: number
  category?: string | null
  hasImage?: boolean
  imageUrl?: string | null
}

export type AdminProductDto = {
  id: string
  name: string
  slug?: string
  description: string | null
  sku: string | null
  priceCents?: number
  price: string
  pointsCost?: number
  stock: number
  isActive: boolean
  hasImage?: boolean
  imageUrl?: string | null
}

export type AdminCouponDto = {
  id: string
  code: string
  title?: string | null
  displayTitle?: string
  type: string
  percent: number | null
  value: number
  maxUses: number | null
  usedCount: number
  startsAt: string | null
  endsAt: string | null
  isActive: boolean
  isValidNow: boolean
  applyShop?: boolean
  applyBooking?: boolean
  applyMembership?: boolean
}

export type GiftCardProductDto = {
  id: string
  name: string
  description: string | null
  salePriceCents: number
  salePrice: string
  giftValueCents: number
  giftValue: string
  currency: string
  validityType: string
  validityMonths: number | null
  validUntil: string | null
  applicableScopes: string[]
  designTemplate: string
  isActive: boolean
  showOnWebsite: boolean
  sortOrder: number
  hasCover: boolean
  coverUrl: string | null
  soldCount?: number
}

export type GiftCardDto = {
  id: string
  productName: string
  displayCode: string
  originalValueCents: number
  originalValue: string
  remainingBalanceCents: number
  remainingBalance: string
  usedCents: number
  used: string
  currency: string
  status: string
  purchasedAt: string | null
  expiresAt: string | null
  buyerName: string | null
  buyerEmail: string
  recipientName: string | null
  recipientEmail: string | null
  personalMessage: string | null
  isRedeemable: boolean
  transactions?: Array<{
    id: string
    type: string
    amountCents: number
    amount: string
    balanceBefore: string
    balanceAfter: string
    note: string | null
    createdAt: string
  }>
}

export type GiftCardBrandDto = {
  salonName: string
  salonSlug: string
  primaryColor: string
  secondaryColor: string
  textColor: string
  backgroundColor: string
  hasLogo: boolean
  logoUrl: string | null
}

export const tenantNotifyApi = {
  get: () =>
    apiFetch<{ data: { notifyEmail: string; enabled: boolean; fromAddress: string } }>(
      '/api/v1/tenant/notification-settings',
    ),
  save: (payload: { notifyEmail: string; enabled: boolean }) =>
    apiFetch<{ data: { notifyEmail: string; enabled: boolean } }>('/api/v1/tenant/notification-settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
}

export type StaffBookingDto = {
  id: string
  status: string
  startsAt: string
  endsAt: string
  branchId: string
  branchName: string
  employeeId: string | null
  employeeName: string | null
  customerName: string
  customerEmail: string
  customerPhone: string | null
  customerNotes: string | null
  customerId?: string | null
  total: string
  currency: string
  freeHaircutRedeemed?: boolean
  paymentId?: string | null
  paymentStatus?: string | null
  paymentMethod?: string | null
  canMarkPaid?: boolean
  items: Array<{ serviceName: string; durationMinutes: number; priceCents: number }>
}

export type ShopOrderDto = {
  id: string
  status: string
  method: string
  amount: string
  amountCents: number
  currency: string
  buyerName: string
  buyerEmail: string
  buyerPhone: string
  buyerAddress: string
  fulfilled: boolean
  canMarkPaid: boolean
  createdAt: string
  paidAt: string | null
  lines: Array<{ name: string; quantity: number; lineTotal: string }>
}

export const staffApi = {
  tenantBookings: (params?: { date?: string; branchId?: string; employeeId?: string; includeCancelled?: boolean }) => {
    const q = new URLSearchParams()
    if (params?.date) q.set('date', params.date)
    if (params?.branchId) q.set('branchId', params.branchId)
    if (params?.employeeId) q.set('employeeId', params.employeeId)
    if (params?.includeCancelled) q.set('includeCancelled', '1')
    const qs = q.toString()
    return apiFetch<{ data: StaffBookingDto[]; meta: { date: string; count: number } }>(
      `/api/v1/tenant/bookings${qs ? `?${qs}` : ''}`,
    )
  },
  setStatus: (id: string, status: string) =>
    apiFetch<{ data: StaffBookingDto }>(`/api/v1/tenant/bookings/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  redeemFreeHaircut: (id: string) =>
    apiFetch<{ data: StaffBookingDto }>(`/api/v1/tenant/bookings/${id}/redeem-free-haircut`, {
      method: 'POST',
    }),
  cancel: (id: string, reason = 'cancelled_by_salon') =>
    apiFetch<{ data: StaffBookingDto }>(`/api/v1/tenant/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  shopOrders: (params?: { status?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return apiFetch<{ data: ShopOrderDto[] }>(`/api/v1/tenant/shop-orders${qs ? `?${qs}` : ''}`)
  },
  markShopOrderPaid: (id: string) =>
    apiFetch<{ data: ShopOrderDto }>(`/api/v1/tenant/shop-orders/${id}/mark-paid`, { method: 'POST' }),
  markPaymentPaid: (id: string) =>
    apiFetch<{ data: { paymentId: string; status: string } }>(`/api/v1/tenant/payments/${id}/mark-paid`, {
      method: 'POST',
    }),
  employeeMarkPaymentPaid: (id: string) =>
    apiFetch<{ data: { paymentId: string; status: string } }>(`/api/v1/employee/payments/${id}/mark-paid`, {
      method: 'POST',
    }),
  branches: () =>
    apiFetch<{
      data: Array<{
        id: string
        name: string
        slug: string
        addressLine: string | null
        city: string | null
        postalCode: string | null
        phone: string | null
        email: string | null
        isActive: boolean
        hasImage: boolean
        imageUrl: string | null
      }>
    }>('/api/v1/tenant/branches'),
  updateBranch: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: BranchDto }>(`/api/v1/tenant/branches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  createBranch: (payload: Record<string, unknown>) =>
    apiFetch<{ data: BranchDto }>('/api/v1/tenant/branches', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  uploadBranchImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: BranchDto }>(`/api/v1/tenant/branches/${id}/image`, { method: 'POST', body })
  },
  updateEmployeeBranch: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: Record<string, unknown> }>(`/api/v1/tenant/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  createEmployee: (payload: Record<string, unknown>) =>
    apiFetch<{ data: Record<string, unknown> }>('/api/v1/tenant/employees', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  uploadEmployeeImage: (id: string, file: File, onProgress?: (pct: number) => void) => {
    const body = new FormData()
    body.append('image', file)
    if (onProgress) {
      return apiUpload<{ data: Record<string, unknown> }>(
        `/api/v1/tenant/employees/${id}/image`,
        body,
        onProgress,
      )
    }
    return apiFetch<{ data: Record<string, unknown> }>(`/api/v1/tenant/employees/${id}/image`, {
      method: 'POST',
      body,
    })
  },
  updateEmployee: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: Record<string, unknown> }>(`/api/v1/tenant/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteEmployeeImage: (id: string) =>
    apiFetch<{ data: Record<string, unknown> }>(`/api/v1/tenant/employees/${id}/image`, {
      method: 'DELETE',
    }),
  updateService: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: { id: string; name: string; durationMinutes: number; price: string; isActive: boolean } }>(
      `/api/v1/tenant/services/${id}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
  createService: (payload: Record<string, unknown>) =>
    apiFetch<{ data: { id: string; name: string; durationMinutes: number; price: string; isActive: boolean } }>(
      '/api/v1/tenant/services',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  deleteService: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/services/${id}`, { method: 'DELETE' }),
  uploadServiceImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: Record<string, unknown> }>(`/api/v1/tenant/services/${id}/image`, { method: 'POST', body })
  },
  services: () =>
    apiFetch<{
      data: Array<{
        id: string
        name: string
        durationMinutes: number
        price: string
        isActive: boolean
        imageUrl?: string | null
      }>
    }>('/api/v1/tenant/services'),
  listProducts: () => apiFetch<{ data: ProductDto[] }>('/api/v1/tenant/products'),
  createProduct: (payload: Record<string, unknown>) =>
    apiFetch<{ data: ProductDto }>('/api/v1/tenant/products', { method: 'POST', body: JSON.stringify(payload) }),
  updateProduct: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: ProductDto }>(`/api/v1/tenant/products/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteProduct: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/products/${id}`, { method: 'DELETE' }),
  listCoupons: () => apiFetch<{ data: AdminCouponDto[] }>('/api/v1/tenant/coupons'),
  createCoupon: (payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminCouponDto }>('/api/v1/tenant/coupons', { method: 'POST', body: JSON.stringify(payload) }),
  updateCoupon: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: AdminCouponDto }>(`/api/v1/tenant/coupons/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteCoupon: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/coupons/${id}`, { method: 'DELETE' }),
  listGiftCardProducts: () =>
    apiFetch<{
      data: GiftCardProductDto[]
      stats: {
        activeProducts: number
        sold: number
        revenueCents: number
        revenue: string
        openBalanceCents: number
        openBalance: string
        activeCards: number
      }
      brand: GiftCardBrandDto
    }>('/api/v1/tenant/gift-card-products'),
  createGiftCardProduct: (payload: Record<string, unknown>) =>
    apiFetch<{ data: GiftCardProductDto }>('/api/v1/tenant/gift-card-products', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateGiftCardProduct: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: GiftCardProductDto }>(`/api/v1/tenant/gift-card-products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteGiftCardProduct: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/gift-card-products/${id}`, { method: 'DELETE' }),
  uploadGiftCardCover: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: GiftCardProductDto }>(`/api/v1/tenant/gift-card-products/${id}/cover`, {
      method: 'POST',
      body,
    })
  },
  listGiftCardSales: () =>
    apiFetch<{ data: GiftCardDto[]; stats: Record<string, number> }>('/api/v1/tenant/gift-cards'),
  getGiftCard: (id: string) => apiFetch<{ data: GiftCardDto }>(`/api/v1/tenant/gift-cards/${id}`),
  lookupGiftCard: (code: string) =>
    apiFetch<{ data: GiftCardDto }>('/api/v1/staff/gift-cards/lookup', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  redeemGiftCard: (payload: { code: string; amount: number; note?: string }) =>
    apiFetch<{ data: GiftCardDto }>('/api/v1/staff/gift-cards/redeem', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  loyaltyEligible: () =>
    apiFetch<{
      data: Array<{
        bookingId: string
        startsAt: string
        branchName: string
        employeeName?: string | null
        serviceNames: string[]
        customer: { id: string; fullName: string; email: string } | null
        loyalty: { points: number; stamps: number; freeHaircuts: number }
      }>
    }>('/api/v1/tenant/loyalty/eligible'),
  stampBooking: (bookingId: string) =>
    apiFetch<{ data: { points: number; stamps: number; freeHaircuts: number } }>(
      `/api/v1/tenant/loyalty/bookings/${bookingId}/stamp`,
      { method: 'POST' },
    ),
  listClubPlans: () => apiFetch<{ data: MembershipPlanDto[] }>('/api/v1/tenant/club-plans'),
  createClubPlan: (payload: Record<string, unknown>) =>
    apiFetch<{ data: MembershipPlanDto }>('/api/v1/tenant/club-plans', { method: 'POST', body: JSON.stringify(payload) }),
  updateClubPlan: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: MembershipPlanDto }>(`/api/v1/tenant/club-plans/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteClubPlan: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/club-plans/${id}`, { method: 'DELETE' }),
  uploadClubPlanImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: MembershipPlanDto }>(`/api/v1/tenant/club-plans/${id}/image`, { method: 'POST', body })
  },
  deleteClubPlanImage: (id: string) =>
    apiFetch<{ data: MembershipPlanDto }>(`/api/v1/tenant/club-plans/${id}/image`, { method: 'DELETE' }),
  listEmployeeTimeOffs: (id: string) =>
    apiFetch<{
      data: Array<{ id: string; type: string; startsOn: string; endsOn: string; reason: string | null }>
      weeklyOffWeekdays: number[]
    }>(`/api/v1/tenant/employees/${id}/time-offs`),
  createEmployeeTimeOff: (id: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: unknown }>(`/api/v1/tenant/employees/${id}/time-offs`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteTimeOff: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/time-offs/${id}`, { method: 'DELETE' }),
  reports: (days = 30) =>
    apiFetch<{
      data: {
        from: string
        to: string
        days: number
        bookingsTotal: number
        bookingsCompleted: number
        bookingsCancelled: number
        bookingsNoShow: number
        noShowRatePercent: number
        byStatus: Record<string, number>
        byDay: Record<string, number>
        revenueCents: number
        revenue: string
        revenueBooking: string
        revenueShop: string
        paymentsCount: number
      }
    }>(`/api/v1/tenant/reports?days=${days}`),
  listCustomers: () =>
    apiFetch<{
      data: Array<{
        email: string
        name: string
        phone: string | null
        bookingsCount: number
        lastVisit: string | null
      }>
    }>('/api/v1/tenant/customers'),
  listWaitlist: (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : ''
    return apiFetch<{ data: WaitlistEntryDto[] }>(`/api/v1/tenant/waitlist${q}`)
  },
  notifyWaitlist: (id: string) =>
    apiFetch<{ data: WaitlistEntryDto }>(`/api/v1/tenant/waitlist/${id}/notify`, { method: 'POST' }),
  cancelWaitlist: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/api/v1/tenant/waitlist/${id}`, { method: 'DELETE' }),
  listReviews: () =>
    apiFetch<{ data: ReviewDto[]; stats: { avg: number; count: number } }>('/api/v1/tenant/reviews'),
  getNoShowPolicy: () =>
    apiFetch<{ data: { enabled: boolean; feeCents: number; autoMarkMinutes: number } }>(
      '/api/v1/tenant/no-show-policy',
    ),
  saveNoShowPolicy: (payload: { enabled?: boolean; feeCents?: number; autoMarkMinutes?: number }) =>
    apiFetch<{ data: { enabled: boolean; feeCents: number; autoMarkMinutes: number } }>(
      '/api/v1/tenant/no-show-policy',
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
  employees: () =>
    apiFetch<{
      data: Array<{
        id: string
        fullName: string
        firstName?: string
        lastName?: string
        title: string | null
        branchId: string
        branchName: string
        isBookable: boolean
        hasUserAccount: boolean
        weeklyOffWeekdays?: number[]
        hasImage?: boolean
        imageUrl?: string | null
      }>
    }>('/api/v1/tenant/employees'),
  employeeMe: () =>
    apiFetch<{
      data: { id: string; fullName: string; title: string | null; branchId: string; branchName: string; salonName: string }
    }>('/api/v1/employee/me'),
  mySchedule: (date?: string) => {
    const q = date ? `?date=${encodeURIComponent(date)}` : ''
    return apiFetch<{ data: StaffBookingDto[]; meta: { date: string; count: number } }>(`/api/v1/employee/bookings${q}`)
  },
  employeeSetStatus: (id: string, status: string) =>
    apiFetch<{ data: StaffBookingDto }>(`/api/v1/employee/bookings/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  employeeRedeemFreeHaircut: (id: string) =>
    apiFetch<{ data: StaffBookingDto }>(`/api/v1/employee/bookings/${id}/redeem-free-haircut`, {
      method: 'POST',
    }),
}

export type ProductDto = {
  id: string
  name: string
  slug: string
  description: string | null
  priceCents: number
  price: string
  pointsCost: number
  stock: number
  isActive?: boolean
  hasImage?: boolean
  imageUrl?: string | null
}

export type MembershipPlanDto = {
  id: string
  name: string
  slug: string
  description: string | null
  priceCents: number
  price: string
  includedUses: number
  visitsPerMonth?: number
  billingCycleDays: number
  priorityBooking?: boolean
  discountPercent?: number
  tier?: string
  sortOrder?: number
  isFeatured?: boolean
  contractHtml?: string | null
  contractPdfUrl?: string | null
  imageUrl?: string | null
  hasImage?: boolean
  isActive?: boolean
  includedServices?: string[]
}

export type MyMembershipDto = {
  id: string
  plan: string
  planId: string
  salonSlug?: string
  salonName?: string
  tier: string
  status: string
  remainingUses: number
  includedUses: number
  usedUses: number
  startsAt: string
  endsAt: string
  autoRenew: boolean
  paymentMethod: string | null
  paymentStatus: string
  amountPaid?: string
  invoiceNumber: string | null
  isUsable?: boolean
  isExhausted?: boolean
  qrImageUrl: string | null
  qrPayload?: string | null
}

export type StaffMembershipScanDto = MyMembershipDto & {
  customerName: string
  customerId?: string
  customerPhotoUrl?: string | null
  customerPhone?: string | null
  visitedToday?: boolean
  visitedTodayAt?: string | null
  canConsume?: boolean
  consumeBlockReason?: string | null
}

export type CustomerInvoiceDto = {
  id: string
  number: string
  kind: string
  label: string
  status: string
  totalCents: number
  total: string
  currency: string
  issuedAt: string
  pdfUrl: string
}

export type ClubSubscribeResponse =
  | { mode?: 'membership'; checkoutUrl?: null }
  | { mode: 'stripe_checkout'; checkoutUrl: string; sessionId?: string }

export type CartItemDto = {
  id: string
  type: string
  referenceId: string
  name: string
  unitPrice: string
  quantity: number
  lineTotal: string
  pointsCost?: number
  linePointsCost?: number
}

export type CartDto = {
  id: string
  items: CartItemDto[]
  subtotal: string
  discount: string
  net?: string
  tax?: string
  taxRate?: number
  total: string
  coupon: string | null
  pointsCostTotal?: number
  redeemableWithPoints?: boolean
}

function cartHeaders(): HeadersInit {
  const token = localStorage.getItem('ilia.cartToken')
  return token ? { 'X-Cart-Token': token } : {}
}

function rememberCartToken(token?: string | null) {
  if (token) localStorage.setItem('ilia.cartToken', token)
}

export const shopApi = {
  products: (slug: string) =>
    apiFetch<{ data: ProductDto[] }>(`/api/v1/salons/${slug}/products`),
  memberships: (slug: string) =>
    apiFetch<{ data: MembershipPlanDto[] }>(`/api/v1/salons/${slug}/memberships`),
  getCart: async () => {
    const res = await apiFetch<{ data: CartDto; cartToken?: string }>('/api/v1/cart', {
      headers: cartHeaders(),
    })
    rememberCartToken(res.cartToken)
    return res
  },
  addItem: async (payload: { type: 'product' | 'membership'; referenceId: string; quantity?: number }) => {
    const res = await apiFetch<{ data: CartDto; cartToken?: string }>('/api/v1/cart/items', {
      method: 'POST',
      headers: cartHeaders(),
      body: JSON.stringify({
        ...payload,
        cartToken: localStorage.getItem('ilia.cartToken'),
      }),
    })
    rememberCartToken(res.cartToken)
    return res
  },
  removeItem: (id: string) =>
    apiFetch<{ data: CartDto }>(`/api/v1/cart/items/${id}`, {
      method: 'DELETE',
      headers: cartHeaders(),
    }),
  updateQuantity: (id: string, quantity: number) =>
    apiFetch<{ data: CartDto }>(`/api/v1/cart/items/${id}`, {
      method: 'PATCH',
      headers: cartHeaders(),
      body: JSON.stringify({ quantity }),
    }),
  applyCoupon: (code: string) =>
    apiFetch<{ data: CartDto }>('/api/v1/cart/coupon', {
      method: 'POST',
      headers: cartHeaders(),
      body: JSON.stringify({ code }),
    }),
  checkout: (payload: {
    pointsToRedeem?: number
    payWithPoints?: boolean
    paymentMethod?: string
    buyerName?: string
    buyerEmail?: string
    buyerPhone?: string
    buyerAddress?: string
  }) =>
    apiFetch<{
      data: {
        mode?: string
        checkoutUrl?: string | null
        paymentId?: string
        items: unknown[]
        totals: unknown
        loyaltyPointsEarned: number
        loyaltyPointsRedeemed?: number
        invoiceId?: string
      }
    }>('/api/v1/cart/checkout', {
      method: 'POST',
      headers: cartHeaders(),
      body: JSON.stringify(payload),
    }),
  myMemberships: () => apiFetch<{ data: MyMembershipDto[] }>('/api/v1/customer/memberships'),
  cancelMembership: (id: string) =>
    apiFetch<{ data: { status: string } }>(`/api/v1/customer/memberships/${id}/cancel`, {
      method: 'POST',
    }),
  loyalty: () =>
    apiFetch<{
      data: {
        points: number
        stamps: number
        freeHaircuts: number
        stampsNeeded: number
        canRequestStampToday: boolean
        hasStampToday: boolean
        pendingRequest: boolean
        pendingRequestId: string | null
      }
    }>('/api/v1/customer/loyalty'),
  requestStamp: () =>
    apiFetch<{
      data: {
        points: number
        stamps: number
        freeHaircuts: number
        canRequestStampToday: boolean
        pendingRequest: boolean
        requestId: string
      }
    }>('/api/v1/customer/loyalty/stamp-request', { method: 'POST' }),
  invoices: () =>
    apiFetch<{ data: CustomerInvoiceDto[] }>('/api/v1/customer/invoices'),
}

export const clubKioskApi = {
  config: async () => {
    const res = await fetch(`${API_URL}/api/v1/public/club/kiosk`)
    const data = (await res.json().catch(() => ({}))) as {
      data?: {
        enabled: boolean
        branches: Array<{ id: string; name: string; city: string | null; latitude: number | null; longitude: number | null }>
      }
      error?: string
    }
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    return data as {
      data: {
        enabled: boolean
        branches: Array<{ id: string; name: string; city: string | null; latitude: number | null; longitude: number | null }>
      }
    }
  },
  scan: async (payload: { qr: string; kioskKey?: string; branchId?: string; lat?: number; lng?: number }) => {
    const headers = new Headers({ 'Content-Type': 'application/json' })
    if (payload.kioskKey) {
      headers.set('X-Club-Kiosk-Key', payload.kioskKey)
    } else {
      const token = getAccessToken()
      if (token) headers.set('Authorization', `Bearer ${token}`)
    }
    const res = await fetch(`${API_URL}/api/v1/public/club/scan`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        qr: payload.qr,
        kioskKey: payload.kioskKey,
        branchId: payload.branchId,
        lat: payload.lat,
        lng: payload.lng,
      }),
    })
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      error?: string
      message?: string
      data?: {
        customerName: string
        plan: string
        tier: string
        remainingUses: number
        includedUses: number
        endsAt: string
        branchName?: string | null
        branchCity?: string | null
        scannedAt: string
      }
    }
    if (!res.ok) {
      throw new Error(data.error || data.message || `HTTP ${res.status}`)
    }
    return data
  },
  adminKiosk: () =>
    apiFetch<{ data: { key: string; enabled: boolean; publicUrl: string; branches: Array<{ id: string; name: string; city: string | null }> } }>(
      '/api/v1/admin/club/kiosk',
    ),
  rotateKiosk: () =>
    apiFetch<{ data: { key: string; enabled: boolean; publicUrl: string } }>('/api/v1/admin/club/kiosk/rotate', {
      method: 'POST',
    }),
  adminVisits: (params?: { q?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.q) qs.set('q', params.q)
    if (params?.limit) qs.set('limit', String(params.limit))
    const q = qs.toString()
    return apiFetch<{
      data: Array<{
        id: string
        scannedAt: string
        scannedAtLabel: string
        customerName: string
        customerEmail: string
        plan: string
        remainingAfter: number
        includedUses: number
        branchName: string | null
        branchCity: string | null
        source: string | null
        clientIp: string | null
      }>
    }>(`/api/v1/admin/club/visits${q ? `?${q}` : ''}`)
  },
}

export const clubApi = {
  subscribe: (payload: {
    planId: string
    paymentMethod?: string
    acceptContract: boolean
    renewFromId?: string
    couponCode?: string
  }) =>
    apiFetch<{ data: MyMembershipDto & ClubSubscribeResponse }>(
      '/api/v1/club/subscribe',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  renew: (renewFromId: string, planId: string, paymentMethod = 'stripe', couponCode?: string) =>
    apiFetch<{ data: MyMembershipDto & ClubSubscribeResponse }>('/api/v1/club/subscribe', {
      method: 'POST',
      body: JSON.stringify({ planId, renewFromId, paymentMethod, acceptContract: true, couponCode }),
    }),
  lookup: (qr: string) =>
    apiFetch<{ data: StaffMembershipScanDto }>('/api/v1/staff/memberships/lookup', {
      method: 'POST',
      body: JSON.stringify({ qr }),
    }),
  consume: (qr: string, branchId?: string) =>
    apiFetch<{ data: StaffMembershipScanDto }>('/api/v1/staff/memberships/verify', {
      method: 'POST',
      body: JSON.stringify({ qr, branchId }),
    }),
  adminListPlans: () => apiFetch<{ data: MembershipPlanDto[] }>('/api/v1/admin/club-plans'),
  adminSavePlan: (id: string | null, payload: Record<string, unknown>) =>
    id
      ? apiFetch<{ data: MembershipPlanDto }>(`/api/v1/admin/club-plans/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      : apiFetch<{ data: MembershipPlanDto }>('/api/v1/admin/club-plans', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
  adminDeletePlan: (id: string) =>
    apiFetch<{ data: MembershipPlanDto; deleted: boolean }>(`/api/v1/admin/club-plans/${id}`, {
      method: 'DELETE',
    }),
  adminUploadPlanImage: (id: string, file: File) => {
    const body = new FormData()
    body.append('image', file)
    return apiFetch<{ data: MembershipPlanDto }>(`/api/v1/admin/club-plans/${id}/image`, {
      method: 'POST',
      body,
    })
  },
}

// ── Branding / Appearance ────────────────────────────────────────────────────

export interface BrandingDto {
  primaryColor: string
  secondaryColor: string
  backgroundColor: string
  textColor: string
  buttonColor: string
  fontFamily: string
  availableFonts: string[]
  heroHeadline: string | null
  heroSubline: string | null
  aboutText: string | null
  metaTitle?: string | null
  metaDescription?: string | null
  instagram: string | null
  facebook: string | null
  tiktok: string | null
  websiteUrl: string | null
  showAbout: boolean
  showServices: boolean
  showTeam: boolean
  showGallery: boolean
  showHours: boolean
  showContact: boolean
  showSocial: boolean
  publishStatus: 'draft' | 'published'
  publishedAt: string | null
  canPublish?: boolean
  subscriptionPaid?: boolean
  siteStatus?: string
  previewToken?: string | null
  salonName?: string
  salonSlug?: string
  hasLogo: boolean
  logoUpdatedAt: string | null
  hasHero: boolean
  heroUpdatedAt: string | null
  updatedAt: string
}

export interface GalleryImageDto {
  id: string
  position: number
  caption: string | null
  mime: string
  createdAt: string
  imageUrl: string
}

export const giftCardsApi = {
  listPublic: (slug: string) =>
    apiFetch<{ data: GiftCardProductDto[]; brand: GiftCardBrandDto }>(
      `/api/v1/public/salons/${encodeURIComponent(slug)}/gift-cards`,
    ),
  checkout: (slug: string, productId: string, payload: Record<string, unknown>) =>
    apiFetch<{ data: { checkoutUrl: string; sessionId: string; giftCardId: string } }>(
      `/api/v1/public/salons/${encodeURIComponent(slug)}/gift-cards/${productId}/checkout`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  purchaseStatus: (slug: string, cardId: string) =>
    apiFetch<{ data: Partial<GiftCardDto> & { id: string; status: string } }>(
      `/api/v1/public/salons/${encodeURIComponent(slug)}/gift-cards/purchase/${cardId}`,
    ),
}

export const appearanceApi = {
  getBranding: () => apiFetch<{ data: BrandingDto }>('/api/v1/appearance/branding'),
  updateBranding: (payload: Partial<BrandingDto>) =>
    apiFetch<{ data: BrandingDto }>('/api/v1/appearance/branding', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  publish: (action: 'publish' | 'draft' = 'publish') =>
    apiFetch<{ data: { publishStatus: string; publishedAt: string | null; canPublish?: boolean } }>('/api/v1/appearance/branding/publish', {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),
  uploadLogo: (file: File) => {
    const fd = new FormData()
    fd.append('logo', file)
    return apiFetch<{ data: { hasLogo: boolean; logoUpdatedAt: string | null } }>('/api/v1/appearance/logo', { method: 'POST', body: fd })
  },
  deleteLogo: () => apiFetch<{ data: { hasLogo: boolean } }>('/api/v1/appearance/logo', { method: 'DELETE' }),
  logoImageUrl: () => '/api/v1/appearance/logo/image',
  /** Prefer preview token URLs in <img> — they are public and don't need Bearer headers. */
  logoPreviewUrl: (previewToken: string, cacheKey?: string | null) =>
    `/api/v1/preview/salon/${encodeURIComponent(previewToken)}/logo${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,
  heroPreviewUrl: (previewToken: string, cacheKey?: string | null) =>
    `/api/v1/preview/salon/${encodeURIComponent(previewToken)}/hero${cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : ''}`,
  galleryPreviewUrl: (previewToken: string, imageId: string) =>
    `/api/v1/preview/salon/${encodeURIComponent(previewToken)}/gallery/${encodeURIComponent(imageId)}/image`,
  uploadHero: (file: File) => {
    const fd = new FormData()
    fd.append('hero', file)
    return apiFetch<{ data: { hasHero: boolean; heroUpdatedAt: string | null } }>('/api/v1/appearance/hero', { method: 'POST', body: fd })
  },
  heroImageUrl: () => '/api/v1/appearance/hero/image',
  listGallery: () => apiFetch<{ data: GalleryImageDto[] }>('/api/v1/appearance/gallery'),
  uploadGalleryImage: (file: File, caption?: string, position?: number) => {
    const fd = new FormData()
    fd.append('image', file)
    if (caption) fd.append('caption', caption)
    if (position !== undefined) fd.append('position', String(position))
    return apiFetch<{ data: GalleryImageDto }>('/api/v1/appearance/gallery', { method: 'POST', body: fd })
  },
  updateGalleryImage: (id: string, payload: { position?: number; caption?: string | null }) =>
    apiFetch<{ data: GalleryImageDto }>(`/api/v1/appearance/gallery/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  deleteGalleryImage: (id: string) =>
    apiFetch<{ data: { deleted: boolean } }>(`/api/v1/appearance/gallery/${id}`, { method: 'DELETE' }),
}

// ── Working Hours / Schedule ─────────────────────────────────────────────────

export interface WorkingHoursDto {
  weekday: number
  isClosed: boolean
  openTime: string | null
  closeTime: string | null
  breakStart: string | null
  breakEnd: string | null
}

export interface SpecialDayDto {
  id: string
  day: string
  isClosed: boolean
  openTime: string | null
  closeTime: string | null
  label: string | null
}

export const scheduleApi = {
  getHours: () => apiFetch<{ data: WorkingHoursDto[] }>('/api/v1/schedule/hours'),
  setHours: (hours: WorkingHoursDto[]) =>
    apiFetch<{ data: { saved: boolean } }>('/api/v1/schedule/hours', { method: 'PUT', body: JSON.stringify(hours) }),
  listSpecialDays: (params?: { from?: string; to?: string }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString() : ''
    return apiFetch<{ data: SpecialDayDto[] }>(`/api/v1/schedule/special-days${qs}`)
  },
  createSpecialDay: (payload: Omit<SpecialDayDto, 'id'>) =>
    apiFetch<{ data: SpecialDayDto }>('/api/v1/schedule/special-days', { method: 'POST', body: JSON.stringify(payload) }),
  deleteSpecialDay: (id: string) =>
    apiFetch<{ data: { deleted: boolean } }>(`/api/v1/schedule/special-days/${id}`, { method: 'DELETE' }),
}

// ── Termination Requests ─────────────────────────────────────────────────────

export interface TerminationRequestDto {
  id: string
  reason: 'sold_salon' | 'permanently_closed' | 'exceptional_reason'
  reasonDetail: string | null
  ownsAnotherSalon: boolean | null
  otherSalonInfo: string | null
  status: 'submitted' | 'under_review' | 'additional_info_required' | 'approved' | 'rejected' | 'scheduled_to_end' | 'completed'
  scheduledEndDate: string | null
  createdAt: string
  updatedAt: string
}

export const terminationApi = {
  list: () => apiFetch<{ data: TerminationRequestDto[] }>('/api/v1/termination'),
  submit: (payload: {
    reason: string
    reasonDetail?: string
    ownsAnotherSalon?: boolean
    otherSalonInfo?: string
  }) =>
    apiFetch<{ data: TerminationRequestDto }>('/api/v1/termination', { method: 'POST', body: JSON.stringify(payload) }),
  get: (id: string) => apiFetch<{ data: TerminationRequestDto }>(`/api/v1/termination/${id}`),
}

// ── Salon Payments (Stripe Connect) ──────────────────────────────────────────

export interface SalonPaymentAccountDto {
  connected: boolean
  verificationStatus: 'not_started' | 'onboarding' | 'incomplete' | 'active' | 'restricted' | 'disabled'
  chargesEnabled: boolean
  payoutsEnabled: boolean
  onboardingCompleted: boolean
  detailsSubmitted?: boolean
  canAcceptPayments: boolean
  accountType?: string
  disabledReason?: string | null
  defaultCurrency?: string
  payoutMethodLast4?: string | null
  payoutMethodType?: string | null
  payoutMethodLabel?: string | null
  platformFeePercent?: string
  onboardingStartedAt?: string | null
  onboardingCompletedAt?: string | null
  lastStripeSyncAt?: string | null
}

export interface StripeConnectStatusDto {
  stripeConfigured: boolean
  connectEnabled: boolean
  liveMode?: boolean
  activationUrl: string | null
  setupUrl: string | null
  canManagePlatform?: boolean
}

export interface PaymentSummaryDto {
  period: string
  from?: string
  to?: string
  grossCents: number
  refundCents: number
  platformFeeCents: number
  netCents: number
  paidOutCents?: number
  outstandingCents?: number
  currency: string
  transactions: Array<{
    bookingId: string | null
    bookingRef: string | null
    customerName: string | null
    grossCents: number
    platformFee: number
    salonAmountCents: number
    refundCents?: number
    currency: string
    status: string
    payoutStatus?: string
    paidAt: string | null
  }>
}

export const salonPaymentsApi = {
  getAccount: () => apiFetch<{ data: SalonPaymentAccountDto }>('/api/v1/payments/account'),
  getConnectStatus: () => apiFetch<{ data: StripeConnectStatusDto }>('/api/v1/payments/connect-status'),
  refreshConnectStatus: () =>
    apiFetch<{ data: StripeConnectStatusDto }>('/api/v1/payments/connect-status/refresh', { method: 'POST' }),
  startOnboarding: (returnUrl?: string, refreshUrl?: string) =>
    apiFetch<{ data: { onboardingUrl: string; stripeAccountId: string } }>('/api/v1/payments/onboard', {
      method: 'POST',
      body: JSON.stringify({
        returnUrl: returnUrl ?? `${window.location.origin}/salon/payments?onboarded=1`,
        refreshUrl: refreshUrl ?? `${window.location.origin}/salon/payments?refresh=1`,
      }),
    }),
  getDashboardLink: () =>
    apiFetch<{ data: { url: string } }>('/api/v1/payments/dashboard-link', { method: 'POST' }),
  getSummary: (
    period: 'today' | 'week' | 'month' | 'custom' = 'month',
    range?: { from: string; to: string },
  ) => {
    const qs = new URLSearchParams({ period })
    if (period === 'custom' && range?.from && range?.to) {
      qs.set('from', range.from)
      qs.set('to', range.to)
    }
    return apiFetch<{ data: PaymentSummaryDto }>(`/api/v1/payments/summary?${qs.toString()}`)
  },
}
